import { MapPin, Mail, ArrowUp } from "lucide-react";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#2C2C2C] text-white relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="/logo-white.png"
              alt="TT Wood - Menuiserie artisanale"
              loading="lazy"
              decoding="async"
              className="h-16 w-auto mb-4" />

            <p className="text-white/70 text-sm leading-relaxed max-w-md mb-6">
              Menuiserie ébénisterie artisanale à Villard-Bonnot. Nous créons des pièces 
              uniques sur mesure avec passion et savoir-faire depuis plus de 
              5 ans.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/ttwood38/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram">

                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true">

                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd" />

                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Liens Rapides</h4>
            <ul className="space-y-3">
              {[
              { href: "#accueil", label: "Accueil" },
              { href: "#apropos", label: "À propos" },
              { href: "#services", label: "Services" },
              { href: "#realisations", label: "Réalisations" },
              { href: "#temoignages", label: "Témoignages" },
              { href: "#contact", label: "Contact" }].
              map((link) =>
              <li key={link.href}>
                  <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-white/70 hover:text-white transition-colors text-sm">

                    {link.label}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-white/50 flex-shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">
                  Villard-Bonnot (38190)
                  <br />
                  Isère, France
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-white/50 flex-shrink-0" />
                <a
                  href="mailto:contact@ttwood38.fr"
                  className="text-white/70 hover:text-white transition-colors text-sm">ttwood38@gmail.com


                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} TTWood.{" "}
            <a 
              href="https://customer-assets.emergentagent.com/job_ttwood-villard/artifacts/ixzmsq89_CGV%20TTWood38_2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors underline"
            >
              Tous droits réservés
            </a>
          </p>
          <p className="text-white/50 text-sm">
            Site réalisé par{" "}
            <a 
              href="https://www.pk0-informatique.fr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors underline"
            >
              PK0 Informatique
            </a>
          </p>
          <p className="text-white/50 text-sm">
            Menuiserie ébénisterie artisanale à Villard-Bonnot
          </p>
        </div>
      </div>

      {/* Back to top button */}
      <button
        onClick={scrollToTop}
        className="absolute right-6 md:right-12 bottom-6 w-12 h-12 rounded-full bg-deep-oak text-white flex items-center justify-center hover:bg-deep-oak/80 transition-colors shadow-lg"
        aria-label="Retour en haut"
        data-testid="back-to-top">

        <ArrowUp className="w-5 h-5" />
      </button>
    </footer>);

};

export default Footer;